import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Avatar,
  Alert,
  Badge,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  People,
  Business,
  PhotoCamera,
  CheckCircle,
  Cancel,
  Pending,
  Upload,
  Visibility
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import ValidadoLogo from '../components/ValidadoLogo';

interface PendingUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  authorizationNotes?: string;
  createdAt: string;
}

interface Employee {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  authorizationStatus: string;
  authorizationDate?: string;
  authorizedBy?: {
    firstName: string;
    lastName: string;
  };
  lastLogin?: string;
  isActive: boolean;
}

interface Company {
  _id: string;
  name: string;
  email: string;
  industry: string;
  size: string;
  totalEmployees: number;
  pendingAuthorizations: number;
  logo?: {
    url: string;
    filename: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CompanyAdminPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Authorization dialog state
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);
  const [authAction, setAuthAction] = useState<'approve' | 'reject'>('approve');
  const [authNotes, setAuthNotes] = useState('');
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    fetchCompanyInfo();
    fetchPendingUsers();
    fetchEmployees();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await fetch('/api/company/info', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompany(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await fetch('/api/company/pending-authorizations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingUsers(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/company/employees?status=approved', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const openAuthDialog = (user: PendingUser, action: 'approve' | 'reject') => {
    setSelectedUser(user);
    setAuthAction(action);
    setAuthNotes(user.authorizationNotes || '');
    setAuthDialogOpen(true);
  };

  const handleAuthorization = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/company/authorize-user/${selectedUser._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          action: authAction,
          notes: authNotes
        })
      });

      if (response.ok) {
        setSuccess(`User ${authAction === 'approve' ? 'approved' : 'rejected'} successfully`);
        setAuthDialogOpen(false);
        fetchPendingUsers();
        fetchEmployees();
        fetchCompanyInfo();
      } else {
        const data = await response.json();
        setError(data.message || 'Authorization failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('logo', logoFile);

    try {
      const response = await fetch('/api/company/logo', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        setSuccess('Logo uploaded successfully');
        setLogoFile(null);
        setLogoPreview(null);
        fetchCompanyInfo();
      } else {
        const data = await response.json();
        setError(data.message || 'Logo upload failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'primary';
      case 'user': return 'default';
      default: return 'default';
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'manager')) {
    return (
      <Container>
        <Alert severity="error">
          Access denied. You must be an admin or manager to view this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <ValidadoLogo width={120} height={48} />
          <Box ml={2}>
            <Typography variant="h4" component="h1">
              Company Administration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {company?.name} - {user.firstName} {user.lastName}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={handleTabChange}>
        <Tab 
          icon={
            <Badge badgeContent={pendingUsers.length} color="error">
              <Pending />
            </Badge>
          } 
          label="Pending Approvals" 
        />
        <Tab icon={<People />} label="Employees" />
        <Tab icon={<Business />} label="Company Settings" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Typography variant="h6" gutterBottom>
          Pending Authorization Requests ({pendingUsers.length})
        </Typography>
        
        {pendingUsers.length === 0 ? (
          <Alert severity="info">
            No pending authorization requests.
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Requested Role</TableCell>
                  <TableCell>Request Date</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingUsers.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>{user.firstName} {user.lastName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role.toUpperCase()} 
                        color={getRoleColor(user.role) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {user.authorizationNotes || 'No message'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => openAuthDialog(user, 'approve')}
                        sx={{ mr: 1 }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => openAuthDialog(user, 'reject')}
                      >
                        Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" gutterBottom>
          Company Employees ({employees.length})
        </Typography>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Authorized By</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((employee) => (
                <TableRow key={employee._id}>
                  <TableCell>{employee.firstName} {employee.lastName}</TableCell>
                  <TableCell>{employee.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={employee.role.toUpperCase()} 
                      color={getRoleColor(employee.role) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={employee.isActive ? 'Active' : 'Inactive'} 
                      color={employee.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {employee.authorizedBy ? 
                      `${employee.authorizedBy.firstName} ${employee.authorizedBy.lastName}` : 
                      'System'
                    }
                  </TableCell>
                  <TableCell>
                    {employee.lastLogin ? 
                      new Date(employee.lastLogin).toLocaleDateString() : 
                      'Never'
                    }
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Company Information
                </Typography>
                {company && (
                  <Box>
                    <Typography><strong>Name:</strong> {company.name}</Typography>
                    <Typography><strong>Email:</strong> {company.email}</Typography>
                    <Typography><strong>Industry:</strong> {company.industry}</Typography>
                    <Typography><strong>Size:</strong> {company.size} employees</Typography>
                    <Typography><strong>Total Employees:</strong> {company.totalEmployees}</Typography>
                    <Typography><strong>Pending Requests:</strong> {company.pendingAuthorizations}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Company Logo
                </Typography>
                
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                  {company?.logo?.url ? (
                    <Avatar
                      src={company.logo.url}
                      sx={{ width: 120, height: 120 }}
                      variant="rounded"
                    />
                  ) : (
                    <Avatar
                      sx={{ width: 120, height: 120, bgcolor: 'grey.300' }}
                      variant="rounded"
                    >
                      <Business sx={{ fontSize: 60 }} />
                    </Avatar>
                  )}
                  
                  {logoPreview && (
                    <Avatar
                      src={logoPreview}
                      sx={{ width: 120, height: 120 }}
                      variant="rounded"
                    />
                  )}
                  
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoFileChange}
                    style={{ display: 'none' }}
                    id="logo-upload"
                  />
                  
                  <Box display="flex" gap={1}>
                    <label htmlFor="logo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCamera />}
                      >
                        Choose Logo
                      </Button>
                    </label>
                    
                    {logoFile && (
                      <Button
                        variant="contained"
                        onClick={handleLogoUpload}
                        disabled={loading}
                        startIcon={<Upload />}
                      >
                        {loading ? 'Uploading...' : 'Upload'}
                      </Button>
                    )}
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary">
                    Recommended: 200x200px, max 5MB
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </TabPanel>

      {/* Authorization Dialog */}
      <Dialog open={authDialogOpen} onClose={() => setAuthDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {authAction === 'approve' ? 'Approve' : 'Reject'} User Request
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Typography gutterBottom>
                <strong>User:</strong> {selectedUser.firstName} {selectedUser.lastName}
              </Typography>
              <Typography gutterBottom>
                <strong>Email:</strong> {selectedUser.email}
              </Typography>
              <Typography gutterBottom>
                <strong>Requested Role:</strong> {selectedUser.role}
              </Typography>
              {selectedUser.authorizationNotes && (
                <Typography gutterBottom>
                  <strong>User Message:</strong> {selectedUser.authorizationNotes}
                </Typography>
              )}
              
              <TextField
                fullWidth
                label="Admin Notes"
                multiline
                rows={3}
                value={authNotes}
                onChange={(e) => setAuthNotes(e.target.value)}
                margin="normal"
                placeholder={`Add notes about this ${authAction}...`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuthDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAuthorization} 
            variant="contained"
            color={authAction === 'approve' ? 'success' : 'error'}
            disabled={loading}
          >
            {loading ? 'Processing...' : (authAction === 'approve' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CompanyAdminPage;